import { useCallback } from 'react';

import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useHasBizyNotification } from './use-has-bizy-notification';

/**
 * Click handler for the Ask Bizy entry point (navbar item, super-admin
 * AskBizyButton, anywhere else this entry point lives). Centralizes the
 * unread-aware engagement semantics:
 *
 *   - Has unread, panel CLOSED → openFromNotificationPeek. The coordinator
 *     auto-loads the proactive conversation and marks it seen on swap.
 *   - Has unread, panel OPEN → plain toggle (close). We deliberately do
 *     NOT mark seen here: a window-focus refetch can deliver a fresh
 *     unread to a panel that's already open, and the user closing the
 *     sidebar shouldn't silently consume a notification they may never
 *     have looked at. The indicator stays on; engagement happens through
 *     the in-chat / peek paths.
 *   - No unread → plain toggle.
 *
 * Lives in one place so the navbar entry and the super-admin button stay
 * in lockstep when the engagement semantics evolve.
 */
export function useBizyAskClick(): () => void {
	const isOpen = useBizySidePanelStore((s) => s.isOpen);
	const toggle = useBizySidePanelStore((s) => s.toggle);
	const openFromNotificationPeek = useBizySidePanelStore((s) => s.openFromNotificationPeek);
	const hasUnread = useHasBizyNotification();

	return useCallback(() => {
		if (hasUnread && !isOpen) {
			openFromNotificationPeek();
			return;
		}
		toggle();
	}, [hasUnread, isOpen, openFromNotificationPeek, toggle]);
}
